import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";

export default function SetPassword() {
  const [params] = useSearchParams();
  const nav = useNavigate();
  const { user, refresh } = useAuth();
  const [stage, setStage] = useState("verifying"); // verifying | form | done | error
  const [error, setError] = useState("");
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    // If a fresh token is in the URL, consume it first to authenticate.
    const token = params.get("token");
    if (!token) {
      // If already logged in (e.g. arrived from /continue), skip the token step.
      if (user) { setStage("form"); return; }
      setStage("error"); setError("no token — request a new link from your inbox.");
      return;
    }
    (async () => {
      try {
        const { data } = await api.post("/auth/magic-link/consume", { token });
        localStorage.setItem("sd_token", data.token);
        await refresh();
        setStage("form");
      } catch (e) {
        setStage("error");
        setError(e?.response?.data?.detail || "could not verify link");
      }
    })();
    // eslint-disable-next-line
  }, [params]);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 6) { toast.error("password must be at least 6 characters"); return; }
    if (password !== confirm) { toast.error("passwords don't match"); return; }
    setBusy(true);
    try {
      await api.patch("/auth/password", { password });
      toast.success("password saved. you're set.");
      setStage("done");
      setTimeout(() => nav("/continue"), 1200);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "could not save password");
    } finally { setBusy(false); }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-6 py-16" data-testid="set-password-page">
      <div className="w-full max-w-md">
        <p className="text-xs uppercase tracking-[0.3em] mb-4" style={{ color: "var(--gold)" }}>
          {stage === "done" ? "welcome" : stage === "error" ? "sign-in link" : "create your account"}
        </p>
        <h1 className="font-serif text-4xl mb-6" style={{ color: "var(--ink)" }}>
          {stage === "verifying" && "one moment..."}
          {stage === "form" && "choose a password."}
          {stage === "done" && "you're all set."}
          {stage === "error" && "hmm."}
        </h1>

        {stage === "verifying" && (
          <p style={{ color: "var(--ink-soft)" }}>verifying your link…</p>
        )}

        {stage === "form" && (
          <>
            <p className="mb-8" style={{ color: "var(--ink-soft)" }}>
              you already have access. set a password so you can sign in with your email from any device.
            </p>
            <form onSubmit={submit} className="space-y-6" data-testid="set-password-form">
              <div>
                <label className="text-xs uppercase tracking-widest" style={{ color: "var(--ink-soft)" }}>new password</label>
                <input
                  type="password" required minLength={6} value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="field" data-testid="set-password-input"
                />
              </div>
              <div>
                <label className="text-xs uppercase tracking-widest" style={{ color: "var(--ink-soft)" }}>confirm</label>
                <input
                  type="password" required minLength={6} value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  className="field" data-testid="set-password-confirm"
                />
              </div>
              <button disabled={busy} className="btn-primary w-full" data-testid="set-password-submit">
                {busy ? "saving..." : "save password"}
              </button>
            </form>
            <p className="text-xs mt-6" style={{ color: "var(--ink-soft)" }}>
              you can also skip this and keep using the email link — <a href="/continue" className="underline">go to the course</a>.
            </p>
          </>
        )}

        {stage === "done" && (
          <p style={{ color: "var(--ink-soft)" }}>opening the course.</p>
        )}

        {stage === "error" && (
          <p style={{ color: "var(--ink-soft)" }} data-testid="set-password-error">{error}</p>
        )}
      </div>
    </div>
  );
}
